/* eslint-disable @typescript-eslint/no-floating-promises */
import Component from '@glimmer/component';
import type { EmberChangeset } from 'ember-changeset';
import type { ForcBaseArgs } from 'parog-web/components/forc/abstract/base-component';
import MultiStepsEditionForm from 'parog-web/components/multi-steps-edition-form';
import type Patient from 'parog-web/models/patient';
import emberSwal from 'ui-kit/utils/ember-swal';
import AbdominalPain from './base-edition-form/abdominal-pain';
import AbdominalPainIntensity from './base-edition-form/abdominal-pain-intensity';
import AnalIncontinence from './base-edition-form/anal-incontinence';
import AnalIncontinenceFrequency from './base-edition-form/anal-incontinence-frequency';
import AnusSymptoms from './base-edition-form/anus-symptoms';
import AnusSymptomsFrequency from './base-edition-form/anus-symptoms-frequency';
import ArticularPain from './base-edition-form/articular-pain';
import ArticularPainIbd from './base-edition-form/articular-pain-ibd';
import ArticularPainSeverity from './base-edition-form/articular-pain-severity';
import AverageStoolsNumber from './base-edition-form/average-stools-number';
import BloodInStool from './base-edition-form/blood-in-stool';
import BloodInStoolFrequency from './base-edition-form/blood-in-stool-frequency';
import BodyDissatisfaction from './base-edition-form/body-dissatisfaction';
import EditionForm from './base-edition-form/edition-form/index';
import EyeSymptoms from './base-edition-form/eye-symptoms';
import EyeSymptomsPainful from './base-edition-form/eye-symptoms-painful';
import EyeSymptomsRedEye from './base-edition-form/eye-symptoms-red-eye';
import EyeSymptomsSight from './base-edition-form/eye-symptoms-sight';
import Fever from './base-edition-form/fever';
import FeverLevel from './base-edition-form/fever-level';
import FistulasEmbarrassment from './base-edition-form/fistulas-embarrassment';
import FistulasFlow from './base-edition-form/fistulas-flow';
import FistulasMovement from './base-edition-form/fistulas-movement';
import FistulasPain from './base-edition-form/fistulas-pain';
import FistulasPainSkin from './base-edition-form/fistulas-pain-skin';
import HasFistulas from './base-edition-form/has-fistulas';
import ImmediateStool from './base-edition-form/immediate-stool';
import ImmediateStoolUrgency from './base-edition-form/immediate-stool-urgency';
import LackOfEnergy from './base-edition-form/lack-of-energy';
import LowMood from './base-edition-form/low-mood';
import MouthUlcers from './base-edition-form/mouth-ulcers';
import RepeatedVomiting from './base-edition-form/repeated-vomiting';
import SexualDifficulties from './base-edition-form/sexual-difficulties';
import SkinSymptoms from './base-edition-form/skin-symptoms';
import SleepDifficulties from './base-edition-form/sleep-difficulties';
import Slider from './base-edition-form/slider';
import SocialDifficulties from './base-edition-form/social-difficulties';
import StomachPain from './base-edition-form/stomach-pain';
import StoolsNumber from './base-edition-form/stools-number';
import TransitBlockage from './base-edition-form/transit-blockage';
import UrgentStoolsMovements from './base-edition-form/urgent-stools-movements';
import Weight from './base-edition-form/weight';
import WellBeing from './base-edition-form/well-being';
import WorkDifficulties from './base-edition-form/work-difficulties';

export const Questions = {
  'abdominal-pain': AbdominalPain,
  'abdominal-pain-intensity': AbdominalPainIntensity,
  'anal-incontinence': AnalIncontinence,
  'anal-incontinence-frequency': AnalIncontinenceFrequency,
  'anus-symptoms': AnusSymptoms,
  'anus-symptoms-frequency': AnusSymptomsFrequency,
  'articular-pain': ArticularPain,
  'articular-pain-ibd': ArticularPainIbd,
  'articular-pain-severity': ArticularPainSeverity,
  'average-stools-number': AverageStoolsNumber,
  'blood-in-stool': BloodInStool,
  'blood-in-stool-frequency': BloodInStoolFrequency,
  'body-dissatisfaction': BodyDissatisfaction,
  'edition-form': EditionForm,
  'eye-symptoms': EyeSymptoms,
  'eye-symptoms-painful': EyeSymptomsPainful,
  'eye-symptoms-red-eye': EyeSymptomsRedEye,
  'eye-symptoms-sight': EyeSymptomsSight,
  fever: Fever,
  'fever-level': FeverLevel,
  'fistulas-embarrassment': FistulasEmbarrassment,
  'fistulas-flow': FistulasFlow,
  'fistulas-movement': FistulasMovement,
  'fistulas-pain': FistulasPain,
  'fistulas-pain-skin': FistulasPainSkin,
  'has-fistulas': HasFistulas,
  'immediate-stool': ImmediateStool,
  'immediate-stool-urgency': ImmediateStoolUrgency,
  'lack-of-energy': LackOfEnergy,
  'low-mood': LowMood,
  'mouth-ulcers': MouthUlcers,
  'repeated-vomiting': RepeatedVomiting,
  'sexual-difficulties': SexualDifficulties,
  'skin-symptoms': SkinSymptoms,
  'sleep-difficulties': SleepDifficulties,
  slider: Slider,
  'social-difficulties': SocialDifficulties,
  'stomach-pain': StomachPain,
  'stools-number': StoolsNumber,
  'transit-blockage': TransitBlockage,
  'urgent-stools-movements': UrgentStoolsMovements,
  weight: Weight,
  'well-being': WellBeing,
  'work-difficulties': WorkDifficulties
};

interface Signature {
  Args: {
    patient: Patient;
    currentSections: Array<string>;
    save: () => Promise<void>;
    changeset: EmberChangeset;
  } & ForcBaseArgs;
}

export default class extends Component<Signature> {
  save = async () => {
    await this.args.save().then(async () => {
      const swalText =
        this.args.patient.healthCentre.studyStatus == 'control_group'
          ? "Merci pour votre contribution, en complétant ce questionnaire vous permettez d’améliorer notre compréhension des soins à distance. Grâce à vous, nous pourrons mieux évaluer l’efficacité des solutions de télésurveillance dans le suivi des patients atteints de maladies chroniques de l’intestin. DigestWise n'est pas un canal d'urgence. Appelez le 112 ou le 15 pour les urgences."
          : "L'équipe soignante va prendre connaissance de vos réponses. DigestWise n'est pas un canal d'urgence. Appelez le 112 ou le 15 pour les urgences.";
      emberSwal.fire({
        text: swalText,
        icon: 'success',
        confirmButtonText: 'Ok'
      });
    });
  };

  <template>
    <MultiStepsEditionForm
      @questions={{Questions}}
      @changeset={{@changeset}}
      @currentSections={{@currentSections}}
      @save={{this.save}}
      @translationPrefix='mici-form'
    />
  </template>
}
